import { Outlet, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import TopBar from "./top-bar.tsx";
import { useDateTimeRangeRefresh } from "@/shared/hooks/use-date-time-range-refresh";
import { BreadcrumbProvider } from "@/shared/contexts/breadcrumb-context";
import { useModal } from "@/shared/lib/modal/modal-context";
import { useEffect, useMemo, useRef } from "react";
import { SidebarProvider, SidebarInset } from "@/ui/components/ui/sidebar";
import { AppSidebar } from "@/ui/components/app-sidebar";
import { SIDEBAR_NAV } from "./sidebar-nav";
import UserMenu from "./user-menu.tsx";
import { useContextualExportHref } from "./use-contextual-export-href";
import { useSidebarStore } from "@/shared/stores/useSidebarStore";
import { useGetOrganizations } from "@/auth/api/queries/user.queries";
import { useLocationTimezoneSync } from "@/settings/hooks/use-location-timezone-sync.ts";
import { useDateTimeRangeZoneSync } from "@/shared/hooks/use-date-time-range-zone-sync";
import { useAlertAlarm } from "@/alerts/hooks/use-alert-alarm";
import { useAlertsLive } from "@/alerts/hooks/use-alerts-live";
import { useActiveAlertScope } from "@/alerts/hooks/use-active-alert-scope";
import { useAlertNotifyFeed } from "@/alerts/hooks/use-alert-notify-feed";
import { ALARM_SCOPE_KEY, playAttentionAsLeader } from "@/alerts/utils/attention-leader";

const DashboardLayout = () => {
  const { t } = useTranslation("common");
  useDateTimeRangeRefresh();
  // One org-scoped settings fetch feeds every location's timezone to the whole
  // app; mounted here so no page has to ask for it.
  useLocationTimezoneSync();
  // Keeps the selected wall-clock window meaning the same hours AT THE SITE when
  // the active location (and so its zone) changes.
  useDateTimeRangeZoneSync();
  // --- Alerts: bell + alarm, app-wide -------------------------------------
  // All three mount HERE because the top-bar bell and the alarm are on every
  // authenticated page, and until now their DATA was not: the feed lived on
  // /map, so navigating anywhere else emptied the bell and silenced the alarm —
  // exactly when it still needs to be heard.
  //
  // The order below is the data flow. Scope resolves the location the queue
  // needs; the live channel supplies low-latency hints for it; the feed reads
  // the queue, fills the bell, and decides whether a hint may sound.
  const alertScope = useActiveAlertScope();
  useAlertsLive(alertScope);
  useAlertNotifyFeed();
  useAlertAlarm(() => void playAttentionAsLeader(ALARM_SCOPE_KEY));

  const [searchParams, setSearchParams] = useSearchParams();
  const { openModal } = useModal();
  const onboardingTriggered = useRef(false);
  const { data: organizationsResponse } = useGetOrganizations();
  const organizations = organizationsResponse?.data ?? [];

  // Persisted collapse state (Zustand) drives the sidebar in controlled mode.
  const sidebarOpen = useSidebarStore((s) => s.open);
  const setSidebarOpen = useSidebarStore((s) => s.setOpen);

  // Keep the sidebar's "Export" item context-aware: on a location/machine page
  // it carries that scope as query params so the export opens predefined.
  const exportHref = useContextualExportHref();
  const navMain = useMemo(
    () =>
      SIDEBAR_NAV.map((group) => ({
        label: t(group.labelKey),
        items: group.items.map((item) => ({
          ...item,
          title: t(item.titleKey),
          url: item.titleKey === "sidebar.items.export" ? exportHref : item.url,
          items: item.items?.map((sub) => ({ ...sub, title: t(sub.titleKey) })),
        })),
      })).filter((group) => group.items.length > 0),
    [exportHref, t],
  );

  useEffect(() => {
    if (searchParams.get("onboarding") === "true" && !onboardingTriggered.current) {
      onboardingTriggered.current = true;
      openModal("onboarding", {});
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, openModal, setSearchParams]);

  return (
    <BreadcrumbProvider>
      <SidebarProvider open={sidebarOpen} onOpenChange={setSidebarOpen}>
        <AppSidebar
          collapsible="icon"
          navMain={navMain}
          organizations={organizations}
          sidebarFooterContent={<UserMenu />}
        />
        {/* min-w-0 lets the inset shrink below its content's intrinsic width so
            wide pages scroll inside <main> instead of widening the whole layout
            (the horizontal-scroll regression). */}
        {/* h-svh + overflow-hidden bounds the inset to the viewport so the inner
            <main> is the single scroll container. Without a definite height the
            shadcn wrapper is only `min-h-svh`, so page-level `overflow`/`h-full`
            never clip and the whole document scrolls instead. */}
        <SidebarInset className="min-w-0 h-svh overflow-hidden">
          <TopBar />
          <main className="flex-1 overflow-auto min-h-0">
            <Outlet />
          </main>
        </SidebarInset>
      </SidebarProvider>
    </BreadcrumbProvider>
  );
};

export default DashboardLayout;
